import { useSearchParams } from "react-router";
import AppPage from "layouts/AppPage";
import { VStack, Box } from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import { useNavbar } from "hooks/useNavbar";
import { PlatformEventFilters } from "features/PlatformEvents";
import EventsTable from "features/PlatformEvents/Components/EventsTable";
import EventFilters from "features/PlatformEvents/Components/EventFilters";
import { useAnalytics } from "services/analytics";
import { EventTypes } from "services/analytics/eventTypes";
import { Heading18 } from "components/Heading";

const DEFAULT_FILTERS: PlatformEventFilters = {
  page: 1,
  perPage: 50,
  sortBy: "occurred_at",
  sortOrder: "desc",
};

export default function ActivityFeedPage() {
  const { setBackLocationPath, setBackLocationName, setPageTitle } = useNavbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();

  // Initialize filters from URL params
  const [filters, setFilters] = useState<PlatformEventFilters>(() => {
    const params: PlatformEventFilters = { ...DEFAULT_FILTERS };
    const eventType = searchParams.get("eventType");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const page = searchParams.get("page");

    if (eventType) params.eventType = eventType as PlatformEventFilters["eventType"];
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (page) params.page = parseInt(page, 10);

    return params;
  });

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.eventType) params.set("eventType", filters.eventType);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate) params.set("endDate", filters.endDate);
    if (filters.page && filters.page > 1) params.set("page", String(filters.page));

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  useEffect(() => {
    setPageTitle("Activity Feed");
    // Track feature usage
    trackEvent(EventTypes.PAGE_VIEW, {
      page: "activity_feed",
      feature_name: "activity_feed",
    });
    return () => {
      setPageTitle(null);
      setBackLocationName(null);
      setBackLocationPath(null);
    };
  }, [setBackLocationName, setBackLocationPath, setPageTitle, trackEvent]);

  const handleFiltersChange = useCallback((newFilters: PlatformEventFilters) => {
    setFilters(newFilters);
    // Track filter usage
    trackEvent(EventTypes.FILTER_CHANGE, {
      feature_name: "activity_feed",
      filters: {
        eventType: newFilters.eventType,
        hasDateRange: !!(newFilters.startDate || newFilters.endDate),
        hasJourneyFilter: !!newFilters.newHireJourneyId,
      },
    });
  }, [trackEvent]);

  const handleReset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return (
    <AppPage>
      <VStack spacing={6} align="stretch">
        <Box mt="4">
          <Heading18>Activity Feed</Heading18>
        </Box>

        <EventFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleReset}
        />

        <EventsTable filters={filters} onFiltersChange={handleFiltersChange} />
      </VStack>
    </AppPage>
  );
}
